import { clear, createStore, del, get, set } from "idb-keyval";

// Two-level caches for tornado chart work: an in-memory LRU, backed by an
// LRU in IndexedDB so results survive page reloads.

export type KeyValueStore = {
  get: (key: string) => Promise<unknown>;
  set: (key: string, value: unknown) => Promise<void>;
  del: (key: string) => Promise<void>;
};

const DB_NAME = "spyglass-tornado-cache";
const STORE_NAME = "results";
const BUILD_KEY = "__build";

// Persisted results are only valid for the calculation code that made them.
// The production worker's file name carries a hash of that code, so it
// identifies the build; when it changes, everything stored is dropped. In
// development there is no such hash, so nothing is persisted.
let persistentStore: Promise<KeyValueStore | undefined> | undefined;
export const getPersistentStore = () =>
  (persistentStore ??= (async () => {
    if (import.meta.env.DEV || typeof indexedDB === "undefined") {
      return undefined;
    }
    try {
      const { default: workerUrl } = await import("./tornadoWorker?worker&url");
      const store = createStore(DB_NAME, STORE_NAME);
      if ((await get(BUILD_KEY, store)) !== workerUrl) {
        await clear(store);
        await set(BUILD_KEY, workerUrl, store);
      }
      return {
        get: key => get(key, store),
        set: (key, value) => set(key, value, store),
        del: key => del(key, store)
      };
    } catch (err) {
      console.warn("Tornado chart cache can't use IndexedDB:", err);
      return undefined;
    }
  })());

// For "Clear Offline Storage".
export const clearPersistedTornadoResults = async () => {
  if (typeof indexedDB === "undefined") return;
  await clear(createStore(DB_NAME, STORE_NAME));
};

export class ResultCache<T> {
  private memory = new Map<string, T>();
  private readonly name: string;
  private readonly memorySize: number;
  private readonly persistentSize: number;
  private readonly store: Promise<KeyValueStore | undefined>;
  // Persistent index updates run one at a time so they can't lose entries.
  private persistentQueue: Promise<unknown> = Promise.resolve();

  constructor(options: {
    name: string;
    memorySize: number;
    persistentSize: number;
    store?: Promise<KeyValueStore | undefined>;
  }) {
    this.name = options.name;
    this.memorySize = options.memorySize;
    this.persistentSize = options.persistentSize;
    this.store = options.store ?? Promise.resolve(undefined);
  }

  // Memory only, so callers can answer synchronously when they can.
  peek(key: string): T | undefined {
    const value = this.memory.get(key);
    if (value !== undefined) this.remember(key, value);
    return value;
  }

  async get(key: string): Promise<T | undefined> {
    const inMemory = this.peek(key);
    if (inMemory !== undefined) return inMemory;
    const store = await this.store;
    if (!store) return undefined;
    try {
      const value = (await store.get(this.entryKey(key))) as T | undefined;
      if (value !== undefined) {
        this.remember(key, value);
        this.touchPersistent(store, key);
      }
      return value;
    } catch {
      return undefined;
    }
  }

  set(key: string, value: T) {
    this.remember(key, value);
    this.store.then(store => {
      if (!store) return;
      this.enqueue(async () => {
        await store.set(this.entryKey(key), value);
        await this.updateIndex(store, key);
      });
    });
  }

  // Resolves once pending persistent writes have finished (for tests).
  flush() {
    return this.store.then(() => this.persistentQueue);
  }

  private remember(key: string, value: T) {
    this.memory.delete(key);
    this.memory.set(key, value);
    while (this.memory.size > this.memorySize) {
      this.memory.delete(this.memory.keys().next().value!);
    }
  }

  private touchPersistent(store: KeyValueStore, key: string) {
    this.enqueue(() => this.updateIndex(store, key));
  }

  private enqueue(operation: () => Promise<void>) {
    this.persistentQueue = this.persistentQueue
      .then(operation)
      .catch(err => console.warn("Tornado chart cache write failed:", err));
  }

  // Keeps an LRU list of stored keys, oldest first, and evicts beyond the cap.
  private async updateIndex(store: KeyValueStore, key: string) {
    const indexKey = `${this.name}:index`;
    const index = ((await store.get(indexKey)) as string[] | undefined) ?? [];
    const updated = [...index.filter(k => k !== key), key];
    const evicted = updated.splice(
      0,
      Math.max(updated.length - this.persistentSize, 0)
    );
    await Promise.all(evicted.map(k => store.del(this.entryKey(k))));
    await store.set(indexKey, updated);
  }

  private entryKey(key: string) {
    return `${this.name}:${key}`;
  }
}
